import { useMemo, useRef } from "react";
import { SecretHitler } from "./games/SecretHitler";
import { Mafia } from "./games/Mafia";
import { Impostor } from "./games/Impostor";
import { Alibi } from "./games/Alibi";
import { LiarDice } from "./games/LiarDice";
import { Spectrum } from "./games/Spectrum";
import type { PlayProps } from "./games/types";
import type { Action } from "../server/protocol";
export { Die } from "./games/shared/Die";
const presenters = {
  "secret-hitler": SecretHitler,
  mafia: Mafia,
  impostor: Impostor,
  alibi: Alibi,
  "liar-dice": LiarDice,
  spectrum: Spectrum,
};
/** Typed dispatcher. One presenter instance per match: phase changes re-render it,
 * so the open private surface (bounded by its own reveal scope), open tools and
 * the live region persist, while phase-scoped drafts live in `shared/drafts`.
 *
 * While the room is paused the server empties `legalActions`. The last actions
 * this seat held in the same match and phase are kept so the control stays
 * mounted, disabled, with its draft intact; nothing can be sent while paused. */
export type ActionCache = { key: string; actions: string[] } | null;
/** While paused, keep showing the actions this seat held in the same match and
 * phase (the server empties them during a pause). Pure, for tests. */
export function pausedActions(
  cache: ActionCache,
  state: PlayProps["state"],
  publicAudience: boolean,
): { cache: ActionCache; state: PlayProps["state"]; frozen: boolean } {
  const g = state.game;
  const key = JSON.stringify([
    state.roomInstanceId ?? "",
    state.playerId,
    state.matchId,
    state.phaseId ?? g.public.phaseKey ?? null,
  ]);
  const next = state.paused ? cache : { key, actions: g.legalActions };
  const frozen =
    !publicAudience &&
    Boolean(state.playerId) &&
    state.paused &&
    !g.legalActions.length &&
    next?.key === key &&
    next.actions.length > 0;
  return {
    cache: next,
    frozen,
    state: frozen
      ? { ...state, game: { ...g, legalActions: next!.actions } }
      : state,
  };
}
/** The send handed to presenters: drops every action while the room is paused. */
export function sendUnlessPaused(
  paused: { current: boolean },
  send: PlayProps["send"],
): PlayProps["send"] {
  return (action: Action) => (paused.current ? undefined : send(action));
}
export function Play(props: PlayProps) {
  const { state } = props;
  const g = state.game;
  const Presenter = presenters[g.id as keyof typeof presenters];
  const cache = useRef<ActionCache>(null);
  const kept = pausedActions(
    cache.current,
    state,
    props.isDisplay || Boolean(state.isSpectator),
  );
  cache.current = kept.cache;
  const shown = kept.state;
  const paused = useRef(state.paused);
  paused.current = state.paused;
  const parentSend = props.send;
  const send = useMemo(
    () => sendUnlessPaused(paused, parentSend),
    [parentSend],
  );
  return Presenter ? (
    <Presenter
      {...props}
      state={shown}
      send={send}
      disabled={props.disabled || state.paused}
      key={`${state.roomInstanceId ?? ""}:${state.playerId ?? "display"}:${state.matchId}`}
    />
  ) : null;
}
